import ActionButton from 'components/ActionButton';
import React, { useEffect, useState } from 'react';
import styles from './index.module.scss';
import * as yup from 'yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers';
import { Error } from 'components/message/Error';
import { createHomePageCollection, getHomePageCollectionDetail, updateHomePageCollection } from 'services/api/homePageCollection';
import { showError, showSuccess } from 'utils';
import { Loading } from 'components/loading/Loading';

const validationSchema = yup.object().shape({
  title: yup.string().required(),
  componentName: yup.string().required('Card Component is a required field'),
  cloudFunctionName: yup.string().required('Cloud Function is a required field'),
  cloudFunctionParams: yup.string().required(),
});

type FormData = {
  title: string;
  componentName: string;
  cloudFunctionName: string;
  cloudFunctionParams: string;
};

const CreateHomePageCollection = props => {
  const [loading, setLoading] = useState<boolean>(false);
  const [fetchLoading, setFetchLoading] = useState<boolean>(false);
  const [active, setActive] = useState<boolean>(false);
  const [collection, setCollection] = useState<Record<string, any>>({});
  const {
    register,
    handleSubmit,
    errors,
    setValue,
  } = useForm<FormData>({ resolver: yupResolver(validationSchema) });
  const { collectionId } = props.match.params;
  const isEdit = collectionId !== 'collection';

  const fetchCollectionDetails = async () => {
    setFetchLoading(true);
    try {
      const response = (await getHomePageCollectionDetail(collectionId)).toJSON();
      setCollection({
        ...response,
        cloudFunctionParams: JSON.stringify(response.cloudFunctionParams),
      });
      setActive(response.active);
    } catch (e) {
      showError(e);
    } finally {
      setFetchLoading(false);
    }
  };

  useEffect(() => {
    if (isEdit) {
      fetchCollectionDetails();
    }
  }, []);

  const getPayload = (formData: FormData) => {
    const {
      cloudFunctionName,
      cloudFunctionParams,
      componentName,
      title,
    } = formData;
    return {
      active,
      title,
      componentName,
      cloudFunctionName,
      cloudFunctionParams: JSON.parse(cloudFunctionParams),
    };
  };

  const createHomePageCollectionFn = handleSubmit(async (values) => {
    setLoading(true);
    try {
      const data = getPayload(values);
      const response = await createHomePageCollection(data);
      showSuccess('Collection created successfully');
      props.history.replace(`/home-page-collections/${response.id}`);
    } catch (e) {
      showError(e);
      setLoading(false);
    }
  });

  const editHomePageCollectionFn = handleSubmit(async (values) => {
    setLoading(true);
    try {
      const data = { ...getPayload(values), id: collectionId };
      await updateHomePageCollection(data);
      showSuccess('Collection details updated successfully');
      props.history.goBack();
    } catch (e) {
      showError(e);
      setLoading(false);
    }
  });
  
  const handleCancel = () => {
    if (isEdit) {
      props.history.replace({
        pathname: `/home-page-collections/${collectionId}`,
        search: props.location.search,
      });
    } else {
      props.history.goBack();
    }
  };
  
  const getFooter = () => (
    <div className={styles['footer']}>
      <ActionButton
        text={'Cancel'}
        active={!loading}
        isLoading={false}
        onClick={handleCancel}
        customClass={styles['cancel']}
      />
      <ActionButton
        text={`${isEdit ? 'Save' : 'Create'}`}
        active={!loading}
        isLoading={loading}
        onClick={isEdit ? editHomePageCollectionFn : createHomePageCollectionFn}
      />
    </div>
  );
  
  const getInputsSection = () => (
    <div className={styles['collection-inputs-section']}>
      <div className={styles['active']}>
        <input
          type={'checkbox'}
          checked={active}
          disabled={loading}
          onChange={e => setActive(e.target.checked)}
        />
        <div className={styles['label']}>Active</div>
      </div>
      <div className={styles['title']}>
        <div className={styles['label']}>Title</div>
        <input
          name={'title'}
          ref={register}
          className={'form-control'}
          disabled={loading}
          defaultValue={collection.title}
        />
        {errors.title && <Error message={errors.title.message} />}
      </div>
      <div className={styles['card-component']}>
        <div className={styles['label']}>Card Component</div>
        <input
          name={'componentName'}
          ref={register}
          className={'form-control'}
          disabled={loading}
          defaultValue={collection.componentName}
        />
        {errors.componentName && <Error message={errors.componentName.message} />}
      </div>
      <div className={styles['cloud-function']}>
        <div className={styles['label']}>Cloud Function</div>
        <input
          name={'cloudFunctionName'}
          ref={register}
          className={'form-control'}
          disabled={loading}
          defaultValue={collection.cloudFunctionName}
        />
        {errors.cloudFunctionName && <Error message={errors.cloudFunctionName.message} />}
      </div>
      <div className={styles['params']}>
        <div className={styles['label']}>Params</div>
        <textarea
          name={'cloudFunctionParams'}
          ref={register}
          className={'form-control'}
          disabled={loading}
          defaultValue={collection.cloudFunctionParams}
        />
        {errors.cloudFunctionParams && <Error message={errors.cloudFunctionParams.message} />}
      </div>
    </div>
  );
  
  const getHeader = () => (
    <div className={styles['header']}>
      <div className={styles['heading']}>{`${isEdit ? 'Edit' : 'Create'} Collection`}</div>
    </div>
  );
  
  if (fetchLoading) return <Loading />;
  
  return (
    <div className={styles['create-collection-container']}>
      {getHeader()}
      {getInputsSection()}
      {getFooter()}
    </div>
  );
};

export default CreateHomePageCollection;